import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';
import { formatBettingLine } from '../../utils/gameDetailUtils';
import styles from './GameDetailModal.module.css';

const buildSyntheticHistory = (game) => {
  const betting = game?.betting || {};
  const points = [];
  if (betting.spread_open != null || betting.over_under_open != null) {
    points.push({
      capturedAt: game.start_date || null,
      spread: betting.spread_open ?? null,
      overUnder: betting.over_under_open ?? null,
      source: 'open',
    });
  }
  if (betting.spread != null || betting.over_under != null) {
    const differs = betting.spread !== betting.spread_open
      || betting.over_under !== betting.over_under_open;
    if (differs || points.length === 0) {
      points.push({
        capturedAt: new Date().toISOString(),
        spread: betting.spread ?? null,
        overUnder: betting.over_under ?? null,
        source: 'current',
      });
    }
  }
  return { gameId: game?.id, points, synthetic: true };
};

const formatAxisTime = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatTooltipTime = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/** Chart-axis / tooltip label — show PK at 0 instead of N/A. */
const formatChartLine = (value) => {
  if (value === null || value === undefined) return 'N/A';
  if (value === 0) return 'PK';
  return formatBettingLine(value);
};

/**
 * True stair-step geometry: horizontal hold, then vertical jump at the same x.
 * Favorite flips insert a PK vertex on that vertical (same x) so green/red meet
 * without a diagonal and without a hoverable "data point" for tooltips.
 */
const buildStepChartData = (points, { colorBySign }) => {
  if (points.length === 0) return [];

  const colorize = (row) => {
    if (!colorBySign) {
      return { ...row, homeFav: null, awayFav: null };
    }
    if (row.value < 0) return { ...row, homeFav: row.value, awayFav: null };
    if (row.value > 0) return { ...row, homeFav: null, awayFav: row.value };
    return { ...row, homeFav: 0, awayFav: 0 };
  };

  const out = [];
  // Micro-offsets keep Recharts' numeric scale happy while reading as a vertical
  const VERT = 0.001;

  out.push(colorize({
    ...points[0],
    x: 0,
    showTick: true,
    isAnchor: true,
  }));

  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1];
    const curr = points[i];
    const jumpX = i; // vertical stack sits on this integer x (matches tick)

    // Horizontal: hold previous print until this capture's x
    out.push(colorize({
      capturedAt: curr.capturedAt,
      label: curr.label,
      value: prev.value,
      source: prev.source,
      x: jumpX,
      showTick: false,
      isStepCorner: true,
    }));

    const crosses = colorBySign
      && prev.value != null
      && curr.value != null
      && prev.value !== 0
      && curr.value !== 0
      && Math.sign(prev.value) !== Math.sign(curr.value);

    if (crosses) {
      // PK handoff on the vertical — not a real print; hide from tooltip/dots
      out.push(colorize({
        capturedAt: curr.capturedAt,
        label: '',
        value: 0,
        source: 'crossing',
        x: jumpX + VERT,
        showTick: false,
        isCrossing: true,
      }));
      out.push(colorize({
        capturedAt: curr.capturedAt,
        label: curr.label,
        value: curr.value,
        source: curr.source,
        x: jumpX + VERT * 2,
        showTick: true,
        isAnchor: true,
      }));
    } else {
      out.push(colorize({
        capturedAt: curr.capturedAt,
        label: curr.label,
        value: curr.value,
        source: curr.source,
        x: jumpX + VERT,
        showTick: true,
        isAnchor: true,
      }));
    }
  }

  return out;
};

const renderAnchorDot = (color, radius = 3.5) => (props) => {
  const { cx, cy, payload } = props;
  if (cx == null || cy == null) return null;
  if (!payload?.isAnchor) return null;
  if (payload.value == null || payload.value === 0) return null;
  if (color === '#4caf50' && payload.value > 0) return null;
  if (color === '#ef5350' && payload.value < 0) return null;

  return (
    <circle
      cx={cx}
      cy={cy}
      r={radius}
      fill={color}
      stroke="#1a1a1a"
      strokeWidth={1}
    />
  );
};

/** Only real prints get a tooltip — hide step corners and synthetic PK handoffs. */
const LinesTooltip = ({ active, payload, metric }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row || !row.isAnchor || row.isCrossing) return null;

  const point = payload.find((p) => p.value != null) || payload[0];
  if (point?.value == null) return null;

  return (
    <div className={styles.linesTooltip}>
      <div className={styles.linesTooltipLabel}>
        {formatTooltipTime(row.capturedAt)}
      </div>
      <div>
        {metric === 'spread' ? 'Spread' : 'Total'}
        {': '}
        {formatChartLine(row.value)}
      </div>
    </div>
  );
};

LinesTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  metric: PropTypes.string,
};

const GameDetailLines = ({ game, lineHistory, loading, error }) => {
  const [metric, setMetric] = useState('spread');

  const history = lineHistory || buildSyntheticHistory(game);

  const chartData = useMemo(() => {
    const raw = (history.points || [])
      .map((point) => {
        const value = metric === 'spread' ? point.spread : point.overUnder;
        if (value == null) return null;
        return {
          capturedAt: point.capturedAt,
          label: formatAxisTime(point.capturedAt) || (point.source === 'open' ? 'Open' : 'Now'),
          value,
          source: point.source,
        };
      })
      .filter(Boolean);

    return buildStepChartData(raw, { colorBySign: metric === 'spread' });
  }, [history.points, metric]);

  const tickXs = useMemo(
    () => chartData.filter((row) => row.showTick).map((row) => Math.round(row.x)),
    [chartData],
  );

  const labelByX = useMemo(() => {
    const map = new Map();
    chartData.forEach((row) => {
      if (row.showTick && row.label) map.set(Math.round(row.x), row.label);
    });
    return map;
  }, [chartData]);

  const openPoint = (history.points || []).find((p) => (
    metric === 'spread' ? p.spread != null : p.overUnder != null
  ));
  const lastHistoryPoint = [...(history.points || [])].reverse().find((p) => (
    metric === 'spread' ? p.spread != null : p.overUnder != null
  ));
  const openValue = openPoint
    ? (metric === 'spread' ? openPoint.spread : openPoint.overUnder)
    : null;
  const currentValue = lastHistoryPoint
    ? (metric === 'spread' ? lastHistoryPoint.spread : lastHistoryPoint.overUnder)
    : null;

  const crossedZero = metric === 'spread'
    && openValue != null
    && currentValue != null
    && Math.sign(openValue) !== 0
    && Math.sign(currentValue) !== 0
    && Math.sign(openValue) !== Math.sign(currentValue);

  return (
    <div className={styles.linesSection}>
      <div className={styles.linesHeader}>
        <div className={styles.splitsModeToggle} role="tablist" aria-label="Line metric">
          <button
            type="button"
            role="tab"
            aria-selected={metric === 'spread'}
            className={`${styles.splitsModeButton} ${metric === 'spread' ? styles.splitsModeActive : ''}`}
            onClick={() => setMetric('spread')}
          >
            Spread
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={metric === 'total'}
            className={`${styles.splitsModeButton} ${metric === 'total' ? styles.splitsModeActive : ''}`}
            onClick={() => setMetric('total')}
          >
            Total
          </button>
        </div>
      </div>

      {loading && <p className={styles.gameLogMessage}>Loading line movement…</p>}
      {!loading && error && <p className={styles.gameLogMessage}>{error}</p>}

      {!loading && !error && chartData.length === 0 && (
        <p className={styles.gameLogMessage}>No line movement data for this game yet.</p>
      )}

      {!loading && !error && chartData.length > 0 && (
        <>
          <div className={styles.linesSummary}>
            <span>Open {formatBettingLine(openValue)}</span>
            <span className={styles.linesSummaryArrow}>→</span>
            <span>Current {formatBettingLine(currentValue)}</span>
            {history.synthetic && (
              <span className={styles.linesSyntheticNote}>open → current</span>
            )}
            {crossedZero && (
              <span className={styles.linesCrossNote}>favorite flipped</span>
            )}
          </div>

          <div className={styles.linesChartWrap}>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.08)" strokeDasharray="3 3" />
                <XAxis
                  dataKey="x"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  ticks={tickXs}
                  tickFormatter={(x) => labelByX.get(Math.round(x)) || ''}
                  tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 11 }}
                  axisLine={{ stroke: 'rgba(255,255,255,0.15)' }}
                  tickLine={false}
                  allowDecimals
                />
                <YAxis
                  tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={40}
                  tickFormatter={formatChartLine}
                  domain={['auto', 'auto']}
                />
                <Tooltip
                  cursor={{ stroke: 'rgba(255,255,255,0.25)' }}
                  isAnimationActive={false}
                  content={(props) => <LinesTooltip {...props} metric={metric} />}
                />
                {metric === 'spread' && (
                  <ReferenceLine
                    y={0}
                    stroke="rgba(255,255,255,0.45)"
                    strokeDasharray="4 4"
                    label={{
                      value: 'PK',
                      fill: 'rgba(255,255,255,0.45)',
                      fontSize: 10,
                      position: 'insideTopRight',
                    }}
                  />
                )}
                {metric === 'spread' ? (
                  <>
                    <Line
                      type="linear"
                      dataKey="homeFav"
                      stroke="#4caf50"
                      strokeWidth={2}
                      dot={renderAnchorDot('#4caf50')}
                      activeDot={renderAnchorDot('#4caf50', 5)}
                      connectNulls={false}
                      isAnimationActive={false}
                      name="homeFav"
                    />
                    <Line
                      type="linear"
                      dataKey="awayFav"
                      stroke="#ef5350"
                      strokeWidth={2}
                      dot={renderAnchorDot('#ef5350')}
                      activeDot={renderAnchorDot('#ef5350', 5)}
                      connectNulls={false}
                      isAnimationActive={false}
                      name="awayFav"
                    />
                  </>
                ) : (
                  <Line
                    type="linear"
                    dataKey="value"
                    stroke="#64b5f6"
                    strokeWidth={2}
                    dot={renderAnchorDot('#64b5f6')}
                    activeDot={renderAnchorDot('#64b5f6', 5)}
                    isAnimationActive={false}
                    name="Total"
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {metric === 'spread' && (
            <div className={styles.linesLegend}>
              <span className={styles.linesLegendHome}>≤ 0 home favored</span>
              <span className={styles.linesLegendAway}>&gt; 0 away favored</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

GameDetailLines.propTypes = {
  game: PropTypes.object.isRequired,
  lineHistory: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
};

export default GameDetailLines;
